import { useFpCampaignCartStore } from '@cart-services/hooks/useFpCampaignCartStore'
import { useFpCampaignCouponService } from '@cart-services/hooks/useFpCampaignCouponService'
import { type MergedCoupon } from '@cart-services/types'
import { RegularGiveawayList } from '@cart-ui/RegularGiveawayList/RegularGiveawayList'
import { RegularGiveawayProgressBar } from '@cart-ui/RegularGiveawayProgressBar/RegularGiveawayProgressBar'
import { AppliedStatus } from '@hooks-api/types/dataModel/coupon'
import { VStack } from '@pets-ui/VStack/VStack'
import { useEffect, useMemo, useRef } from 'react'
import { useFormContext } from 'react-hook-form'
import { CartCard } from '../CartCard/CartCard'
import { CartCardBody } from '../CartCardBody/CartCardBody'
import { CartCardHeader } from '../CartCardHeader/CartCardHeader'
import { useContextCampaignCart } from '../CartProvider/CampaignCartProvider'
import { type CartFormValues } from '../CartProvider/CartFormProvider'
import { RegularGiveawaySelectController } from './RegularGiveawaySelectController'
import { Skeletons } from './Skeletons'
export const CartRegularGiveawayTips = () => {
  const { campaignId, countryCode, campaignCode } = useContextCampaignCart()
  const [cartValues] = useFpCampaignCartStore(campaignId)
  const { regularGiveaways, getRegularGiveawayProgress, removeSelectedRegularGiveaways, isLoading: isLoadingCoupons } = useFpCampaignCouponService(
    campaignId,
    countryCode,
    campaignCode,
  )
  const { percentage, totalCriterions, currentCriterions } = useMemo(() => getRegularGiveawayProgress(regularGiveaways ?? []), [regularGiveaways, getRegularGiveawayProgress])
  const { setValue, clearErrors } = useFormContext<CartFormValues>()

  const _regularGiveaways = useRef<MergedCoupon[]>(undefined)
  /**
   * 當原本已選擇的滿額禮不符合資格時，需清除不符合資格之滿額禮
   */
  useEffect(() => {
    if (JSON.stringify(_regularGiveaways.current) === JSON.stringify(regularGiveaways)) return
    _regularGiveaways.current = regularGiveaways
    const filterUnappliedSelectedRegularGiveaways = regularGiveaways?.flatMap(regularGiveaway =>
      regularGiveaway.criterions
        .filter(criterion => criterion.applied_status !== AppliedStatus.APPLIED)
        .map(criterion => criterion.id),
    ) ?? []
    if (filterUnappliedSelectedRegularGiveaways.length > 0) {
      removeSelectedRegularGiveaways(filterUnappliedSelectedRegularGiveaways)
    }

    for (const [index, criterion] of (regularGiveaways?.flatMap(regularGiveaway => regularGiveaway.criterions) ?? []).entries()) {
      if (criterion.applied_status !== AppliedStatus.APPLIED) {
        setValue(`regularGiveaways.${index}`, undefined)
        clearErrors(`regularGiveaways.${index}`)
      }
    }
  }, [clearErrors, regularGiveaways, removeSelectedRegularGiveaways, setValue])

  const regularGiveawayList = useMemo(() => regularGiveaways?.flatMap(regularGiveaway => regularGiveaway.criterions), [regularGiveaways])

  const unappliedRegularGiveawayCount = useMemo(() => regularGiveawayList?.filter(criterion => criterion.applied_status !== AppliedStatus.APPLIED), [regularGiveawayList])
  const appliedRegularGiveawayCount = (regularGiveawayList?.length ?? 0) - (unappliedRegularGiveawayCount?.length ?? 0)

  const maxLength = appliedRegularGiveawayCount + 1
  if (!cartValues.isGiveawayDispatch) return null
  if (isLoadingCoupons) return <Skeletons title="滿額禮" />
  if (!Array.isArray(regularGiveaways) || regularGiveaways.length === 0) return null

  return (
    <CartCard>
      <CartCardHeader title="滿額禮" />
      <CartCardBody>
        <VStack gap={2}>
          <RegularGiveawayProgressBar
            total={totalCriterions}
            current={currentCriterions}
            value={percentage}
          />
          <RegularGiveawayList
            maxLength={maxLength}
          >
            {regularGiveawayList?.map((criterion, index) => (
              <RegularGiveawaySelectController
                key={criterion.id}
                index={index}
                criterion={criterion}
                isLastItem={index === regularGiveawayList?.length - 1}
              />
            ))}
          </RegularGiveawayList>
        </VStack>
      </CartCardBody>
    </CartCard>
  )
}
